import { useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ApiClientService, useApiClient, ApiClientEndpointsByService, } from 'libs.react_internal';
import { SIGNATURE_FIELD_AGENT_ID, SIGNATURE_FIELD_ANALYZE_PROMPT } from './consent_agent.types';
import { mapAgentSignatureFieldsToConsentElements } from './map_agent_fields_to_elements';
import { parseSignatureFieldsFromEvents } from './parse_agent_results';
import { parseSignatureFieldsFromVercelStream } from './vercel_agent_stream';
import { ConsentLogEvents } from '../constants/consent_log_events.constants';
import { useConsentEventLogger } from '../hooks/use_consent_event_logger.hook';
const multipartTransform = {
    transformRequest: [
        (data, headers) => {
            if (typeof FormData !== 'undefined' && data instanceof FormData) {
                delete headers['Content-Type'];
            }
            return data;
        },
    ],
};
async function dataUrlToFile(dataUrl, filename) {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type || 'application/pdf' });
}
export function useConsentSignatureAgent() {
    const agentStudioClient = useApiClient(ApiClientService.AGENT_STUDIO_API);
    const { logConsentEvent } = useConsentEventLogger();
    const sessionIdRef = useRef(uuidv4());
    const locateSignatureFields = useCallback(async (params) => {
        if (params.documentName === '') {
            logConsentEvent(ConsentLogEvents.SIG_FIELDS_EMPTY_DOC_NAME);
            throw new Error('Consent document name is required to analyze signature fields.');
        }
        const file = await dataUrlToFile(params.pdfDataUrl, `${params.documentName}.pdf`);
        const formData = new FormData();
        formData.append('files', file);
        const uploadRes = await agentStudioClient.post('files', formData, multipartTransform);
        const uploaded = uploadRes.data.files?.[0];
        if (!uploaded?.id) {
            throw new Error('Agent Studio file upload did not return a file id');
        }
        const sessionFromUpload = uploadRes.data.sessionId;
        if (sessionFromUpload) {
            sessionIdRef.current = sessionFromUpload;
        }
        const messageText = `${SIGNATURE_FIELD_ANALYZE_PROMPT}\n<file-med>${uploaded.id}</file-med>`;
        const baseUrl = ApiClientEndpointsByService[ApiClientService.AGENT_STUDIO_API];
        const streamUrl = `${baseUrl}/sessions/messages`;
        const streamHeaders = {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
        };
        if (process.env.SERVICE_VERSION) {
            streamHeaders['X-Svc-Version'] = process.env.SERVICE_VERSION;
        }
        const response = await fetch(streamUrl, {
            method: 'POST',
            credentials: 'include',
            headers: streamHeaders,
            body: JSON.stringify({
                agent_id: SIGNATURE_FIELD_AGENT_ID,
                session_id: sessionIdRef.current,
                messages: [{ type: 'text', text: messageText }],
            }),
        });
        const headerSession = response.headers.get('x-session-id') ?? response.headers.get('X-Session-Id');
        if (headerSession) {
            sessionIdRef.current = headerSession;
        }
        if (!response.ok) {
            const errBody = await response.text().catch(() => '');
            throw new Error(errBody || `Agent Studio request failed (${response.status})`);
        }
        const contentType = response.headers.get('content-type') ?? '';
        let parsed;
        if (contentType.includes('application/json')) {
            const data = (await response.json());
            const events = data.events ?? [];
            parsed = parseSignatureFieldsFromEvents(events);
        }
        else {
            parsed = await parseSignatureFieldsFromVercelStream(response.body);
        }
        return mapAgentSignatureFieldsToConsentElements(parsed.fields, params.pdfDataUrl);
    }, [agentStudioClient, logConsentEvent]);
    return { locateSignatureFields };
}
